/**
 * Decides whether a SignalR toaster message is a repeat of one the user
 * has already seen.
 *
 * The rule is one toast per entity per duplication period. Create and Update
 * (and Delete) for that entity share the period, so the action is never part
 * of the identity. A different entity, such as a note on the same ticket,
 * still gets its own toast.
 *
 * ToastNotification keeps the "already shown" times in a Map and calls these
 * helpers. Nothing here reads React state or shows a toast.
 */

/**
 * Fallback toaster window when the env setting is missing or invalid.
 * The configured value is in seconds. Callers compare timestamps in
 * milliseconds, so getNotificationDuplicationPeriodMs converts it.
 */
export const DEFAULT_NOTIFICATION_DUPLICATION_PERIOD_SECONDS = 60

/**
 * Resolves how long to suppress a repeat toast for the same entity.
 *
 * Reads REACT_APP_NOTIFICATION_DUPLICATION_PERIOD, which is a number of
 * seconds (60 in the env files). Returns that duration in milliseconds so
 * it can be compared with Date.now().
 *
 * A blank, non-numeric, or negative value falls back to 30 seconds.
 * Zero is kept as zero: the toast is shown every time and dedupe is off.
 * The value is read at call time, so a test can pass a raw value or set
 * the env var without reloading this module.
 *
 * @param {string|number|undefined|null} [rawValue] - Period in seconds.
 *   Defaults to the env var.
 * @returns {number} Period in milliseconds. Never negative.
 */
export function getNotificationDuplicationPeriodMs(
    rawValue = process.env.REACT_APP_NOTIFICATION_DUPLICATION_PERIOD
) {
    if (rawValue === undefined || rawValue === null || String(rawValue).trim() === '') {
        return DEFAULT_NOTIFICATION_DUPLICATION_PERIOD_SECONDS * 1000
    }

    const seconds = Number(rawValue)
    if (!Number.isFinite(seconds) || seconds < 0) {
        return DEFAULT_NOTIFICATION_DUPLICATION_PERIOD_SECONDS * 1000
    }

    return seconds * 1000
}

/**
 * Builds the identity used to decide "have we already toasted this?".
 *
 * Prefers the SignalR entity, for example "Ticket:100" or "TicketNote:77".
 * The action is left out on purpose. Ticket Created and Ticket Updated for
 * id 100 produce the same key, so the second one is the duplicate.
 *
 * When the payload has no entity type or entity id, the key falls back to
 * the ticket the toast links to ("ticket:100"). A note and its parent
 * ticket stay distinct because their entity ids differ.
 *
 * If that ticket id is missing too, the key is an empty string. Callers
 * treat an empty key as "do not dedupe", so unrelated notifications are
 * not collapsed onto one shared "ticket:undefined" identity.
 *
 * @param {object} [notification] - Fields carried on the notification.
 * @param {string} [notification.entityType] - SignalR entity, such as Ticket or TicketNote.
 * @param {string|number} [notification.entityId] - Id of that entity, not the action.
 * @param {string|number} [notification.ticketId] - Ticket the toast links to. Used only as a fallback.
 * @returns {string} Stable key for the duplication Map, or '' when there is nothing safe to key on.
 */
export function notificationEntityKey({ entityType, entityId, ticketId } = {}) {
    if (entityType != null && entityType !== '' && entityId != null && entityId !== '') {
        return `${entityType}:${entityId}`
    }
    if (ticketId == null || ticketId === '') {
        return ''
    }
    return `ticket:${ticketId}`
}

/**
 * Reports whether this entity was already toasted inside the current window.
 *
 * recentToasts maps an entity key to the Date.now() value recorded when
 * that toast was shown. A later event is a duplicate only while
 * (now - shownAt) is still less than the period. Once the period has
 * elapsed, including the exact boundary, this returns false and the
 * caller may toast again.
 *
 * A missing history map or a missing key is not a duplicate. A period of
 * 0 or less is not a duplicate either, which is how the env setting turns
 * the control off.
 *
 * @param {Map<string, number>} recentToasts - Entity key to the time it was last toasted.
 * @param {string} entityKey - Result of notificationEntityKey.
 * @param {number} now - Current time in milliseconds.
 * @param {number} periodMs - Window from getNotificationDuplicationPeriodMs.
 * @returns {boolean} True when the toast should be skipped.
 */
export function isDuplicateEntityNotification(recentToasts, entityKey, now, periodMs) {
    if (!recentToasts || !entityKey || !(periodMs > 0)) return false
    const shownAt = recentToasts.get(entityKey)
    return shownAt != null && (now - shownAt) < periodMs
}

/**
 * Remembers that a toast was shown for this entity, and drops expired entries.
 *
 * Call this only after the toast is actually shown. A suppressed duplicate
 * must not refresh the timestamp, or the window would keep extending.
 *
 * Entries whose window has already elapsed are removed first, so the Map
 * does not keep every entity seen in the session. Keys are collected
 * before any delete, so the Map is not changed while it is being read.
 * The current entity is then stored against `now`, which starts its
 * duplication period.
 *
 * A period of 0 or less means dedupe is off. With that period,
 * now - shownAt >= periodMs would be true for every existing entry, so
 * this returns before reading or changing the Map.
 *
 * @param {Map<string, number>} recentToasts - Same Map read by isDuplicateEntityNotification. Mutated in place.
 * @param {string} entityKey - Result of notificationEntityKey. Ignored when empty.
 * @param {number} now - Time the toast was shown, in milliseconds.
 * @param {number} periodMs - Window used to decide which older entries can be removed. Zero or less leaves the Map untouched.
 * @returns {void}
 */
export function recordEntityNotification(recentToasts, entityKey, now, periodMs) {
    if (!recentToasts || !entityKey || !(periodMs > 0)) return
    const expiredKeys = []
    for (const [key, shownAt] of recentToasts) {
        if (now - shownAt >= periodMs) {
            expiredKeys.push(key)
        }
    }
    expiredKeys.forEach((key) => recentToasts.delete(key))
    recentToasts.set(entityKey, now)
}
